import React, { useState, useEffect } from "react";
import {
  Button,
  Input,
  Space,
  Typography,
  Card,
  message,
  Modal,
  Switch,
  Popconfirm,
  Skeleton,
  Segmented,
} from "antd";
import {
  PlusOutlined,
  ArrowLeftOutlined,
  SaveOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import {
  getAttractorLoops,
  createAttractorLoop,
  getAttractorLoopDetails,
  saveAttractorLoopDetails,
  deleteAttractorLoop,
} from "../services/attractorservice";
import {
  AttractorImageDto,
  AttractorLoopListDto,
  AttractorLoopSaveDto,
} from "../types/attractortypes";
import { getDropdownUsers } from "../services/userservice";
import { UserDropdownDto } from "../types/userypes";
import { useAuth } from "../auth/authcontext";
import FormButton from "../components/common/formbutton";
import CancelButton from "../components/common/cancelbutton";

import AttractorSlideManager from "../components/attractor/attractorslidemanager";
import AttractorStoreManager from "../components/attractor/attractorstoremanager";
import AttractorTable from "../components/attractor/attractortable";

const { Text, Title } = Typography;
const { Search } = Input;

interface AttractorProps {
  handleUnsavedChanges?: (hasChanges: boolean) => void;
}

const Attractor: React.FC<AttractorProps> = ({ handleUnsavedChanges }) => {
  const { userId, isReadOnly } = useAuth();
  const currentUserGuid = userId || "";

  const [loading, setLoading] = useState(false);
  const [attractorLoops, setAttractorLoops] = useState<AttractorLoopListDto[]>(
    [],
  );
  const [editAttractorLoopId, setEditAttractorLoopId] = useState<string | null>(
    null,
  );
  const [editLoading, setEditLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  // Edit states
  const [attractorLoopName, setAttractorLoopName] = useState("");
  const [attractorLoopActive, setAttractorLoopActive] = useState(true);
  const [attractorLoopImages, setAttractorLoopImages] = useState<
    AttractorImageDto[]
  >([]);
  const [assignedStores, setAssignedStores] = useState<string[]>([]);
  const [initialStateStr, setInitialStateStr] = useState<string | null>(null);

  // Dropdown caches
  const [users, setUsers] = useState<UserDropdownDto[]>([]);

  // Popconfirm creation states
  const [isCreatePopconfirmOpen, setIsCreatePopconfirmOpen] = useState(false);
  const [newLoopName, setNewLoopName] = useState("");
  const [createLoading, setCreateLoading] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchAttractorLoops(1, pageSize, searchText, statusFilter);
    fetchDropdowns();
  }, []);

  useEffect(() => {
    if (!editAttractorLoopId) {
      handleUnsavedChanges?.(false);
      return;
    }
    if (initialStateStr) {
      // Compare current state to initial state
      const currentStr = JSON.stringify({
        name: attractorLoopName,
        active: attractorLoopActive,
        images: attractorLoopImages,
        assignedStores,
      });
      const isDirty = currentStr !== initialStateStr;
      handleUnsavedChanges?.(isDirty);

      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        if (isDirty) {
          e.preventDefault();
          e.returnValue = "";
        }
      };
      window.addEventListener("beforeunload", handleBeforeUnload);
      return () =>
        window.removeEventListener("beforeunload", handleBeforeUnload);
    }
  }, [
    attractorLoopName,
    attractorLoopActive,
    attractorLoopImages,
    assignedStores,
    initialStateStr,
    editAttractorLoopId,
    handleUnsavedChanges,
  ]);

  const fetchAttractorLoops = async (
    page = currentPage,
    size = pageSize,
    search = searchText,
    status = statusFilter,
  ) => {
    setLoading(true);
    try {
      const activeParam = status === "all" ? null : status === "active";
      const result = await getAttractorLoops(page, size, search, activeParam);
      setAttractorLoops(result.data);
      setTotalCount(result.totalCount);
      setCurrentPage(page);
      setPageSize(size);
    } catch (error) {
      console.error("Failed to load attractor attractorLoops:", error);
      message.error("Failed to load attractor attractorLoops.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdowns = async () => {
    try {
      const usersRes = await getDropdownUsers();
      setUsers(usersRes.data);
    } catch (error) {
      console.error("Failed to load dropdown requirements:", error);
    }
  };

  const handleSearch = (value: string) => {
    const trimmed = value.trim();
    setSearchText(trimmed);
    fetchAttractorLoops(1, pageSize, trimmed, statusFilter);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      setSearchText("");
      fetchAttractorLoops(1, pageSize, "", statusFilter);
    }
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    fetchAttractorLoops(1, pageSize, searchText, value);
  };

  const handleTableChange = (pagination: any) => {
    fetchAttractorLoops(
      pagination.current,
      pagination.pageSize,
      searchText,
      statusFilter,
    );
  };

  const getUserName = (guid: string) => {
    if (!guid) return "System User";
    const user = users.find((u) => u.guid.toLowerCase() === guid.toLowerCase());
    return user ? user.fullName : guid;
  };

  const handleCreateLoop = async () => {
    if (!newLoopName.trim()) {
      message.warning("Please enter a valid attractor loop name.");
      return;
    }

    setCreateLoading(true);
    try {
      const newGuid = await createAttractorLoop(
        newLoopName.trim(),
        currentUserGuid,
      );
      message.success("Attractor loop created successfully!");
      setIsCreatePopconfirmOpen(false);
      setNewLoopName("");
      // Open the loop directly for editing
      handleOpenEdit(newGuid);
      fetchAttractorLoops();
    } catch (error: any) {
      console.error("Failed to create loop:", error);
      message.error(error?.response?.data || "Failed to create loop.");
    } finally {
      setCreateLoading(false);
    }
  };

  const handleOpenEdit = async (guid: string) => {
    setEditAttractorLoopId(guid);
    setEditLoading(true);
    try {
      const details = await getAttractorLoopDetails(guid);
      if (details) {
        setAttractorLoopName(details.name);
        setAttractorLoopActive(details.active);
        setAttractorLoopImages(details.images);
        setAssignedStores(details.assignedStoreNumbers || []);
        setInitialStateStr(
          JSON.stringify({
            name: details.name,
            active: details.active,
            images: details.images,
            assignedStores: details.assignedStoreNumbers || [],
          }),
        );
      }
    } catch (error) {
      console.error("Failed to load loop details:", error);
      message.error("Failed to load attractor loop details.");
      setEditAttractorLoopId(null);
    } finally {
      setEditLoading(false);
    }
  };

  const handleSaveAttractorLoop = async () => {
    if (!attractorLoopName.trim()) {
      message.warning("Loop name cannot be empty.");
      return;
    }

    if (!editAttractorLoopId) return;

    setSaveLoading(true);
    try {
      const payload: AttractorLoopSaveDto = {
        name: attractorLoopName.trim(),
        active: attractorLoopActive,
        images: attractorLoopImages.map((slide) => ({
          guid: slide.guid?.startsWith("temp-") ? undefined : slide.guid,
          imageBase64: slide.imageBase64,
          imageUrl: slide.imageUrl,
          fileName: slide.fileName,
          contentType: slide.contentType,
          createdBy: slide.createdBy || currentUserGuid || "System User",
          dateCreated: slide.dateCreated,
        })),
        assignedStoreNumbers: assignedStores,
      };

      await saveAttractorLoopDetails(
        editAttractorLoopId,
        payload,
        currentUserGuid,
      );
      message.success("Attractor loop updated successfully!");
      setInitialStateStr(null);
      handleUnsavedChanges?.(false);
      setEditAttractorLoopId(null);
      fetchAttractorLoops();
    } catch (error: any) {
      console.error("Failed to save loop details:", error);
      message.error(error?.response?.data || "Failed to save loop updates.");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCancelEdit = () => {
    const currentStr = JSON.stringify({
      name: attractorLoopName,
      active: attractorLoopActive,
      images: attractorLoopImages,
      assignedStores,
    });
    if (initialStateStr && currentStr !== initialStateStr) {
      Modal.confirm({
        title: "Unsaved Changes",
        icon: <ExclamationCircleOutlined />,
        content:
          "You have unsaved changes. Are you sure you want to discard them?",
        okText: "Yes, discard changes",
        cancelText: "No, keep editing",
        okType: "danger",
        onOk: () => {
          setInitialStateStr(null);
          handleUnsavedChanges?.(false);
          setEditAttractorLoopId(null);
        },
      });
    } else {
      setInitialStateStr(null);
      handleUnsavedChanges?.(false);
      setEditAttractorLoopId(null);
    }
  };

  const handleDeleteAttractorLoop = async (guid: string) => {
    try {
      await deleteAttractorLoop(guid);
      message.success("Attractor loop deleted successfully.");
      fetchAttractorLoops();
    } catch (error) {
      console.error("Failed to delete attractor loop:", error);
      message.error("Failed to delete attractor loop.");
    }
  };

  if (editAttractorLoopId) {
    return (
      <div style={{ padding: 24 }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "24px",
          }}
        >
          <Space size="large">
            <Button
              shape="circle"
              icon={<ArrowLeftOutlined />}
              onClick={handleCancelEdit}
            />
            <div>
              <Title level={2} style={{ margin: 0 }}>
                Configure Attractor Loop
              </Title>
              <Text type="secondary">
                Update slide content and select store assignments
              </Text>
            </div>
          </Space>
          <Space>
            <CancelButton onClick={handleCancelEdit} />
            <FormButton
              type="primary"
              title="Save Changes"
              loading={saveLoading}
              onClick={handleSaveAttractorLoop}
              icon={<SaveOutlined />}
            />
          </Space>
        </div>

        {editLoading ? (
          <div
            style={{ display: "flex", gap: "24px", flexDirection: "column" }}
          >
            <Card style={{ borderRadius: "8px" }}>
              <Skeleton
                active
                title={false}
                paragraph={{ rows: 2, width: ["30%", "100%"] }}
              />
            </Card>
            <Card style={{ borderRadius: "8px" }}>
              <Skeleton
                active
                title={false}
                paragraph={{ rows: 2, width: ["20%", "100%"] }}
              />
            </Card>
            <Card style={{ borderRadius: "8px" }}>
              <Skeleton.Button
                active
                style={{ width: 120, height: 120, marginBottom: 16 }}
                shape="round"
              />
              <Skeleton active title={false} paragraph={{ rows: 2 }} />
            </Card>
          </div>
        ) : (
          <div
            style={{ display: "flex", gap: "24px", flexDirection: "column" }}
          >
            {/* Metadata config */}
            <Card
              title="Basic Details"
              bodyStyle={{ padding: "24px" }}
              style={{ borderRadius: "8px" }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "24px",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ flexGrow: 1, minWidth: "300px" }}>
                  <Text
                    strong
                    style={{ display: "block", marginBottom: "8px" }}
                  >
                    Loop Name
                  </Text>
                  <Input
                    placeholder="e.g. Summer Promo Loop"
                    value={attractorLoopName}
                    onChange={(e) => setAttractorLoopName(e.target.value)}
                    size="large"
                  />
                </div>
                <div>
                  <Text
                    strong
                    style={{ display: "block", marginBottom: "8px" }}
                  >
                    Status
                  </Text>
                  <Space align="center" style={{ height: "40px" }}>
                    <Switch
                      size="small"
                      checked={attractorLoopActive}
                      onChange={(checked) => setAttractorLoopActive(checked)}
                    />
                    <Text strong>
                      {attractorLoopActive ? "Active" : "Inactive"}
                    </Text>
                  </Space>
                </div>
              </div>
            </Card>

            {/* Store Assignments Component */}
            <AttractorStoreManager
              assignedStores={assignedStores}
              onChange={setAssignedStores}
            />

            {/* Slide Playlist Component */}
            <AttractorSlideManager
              images={attractorLoopImages}
              onChange={setAttractorLoopImages}
              getUserName={getUserName}
              currentUserGuid={currentUserGuid}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <div>
          <Title level={2} style={{ margin: 0 }}>
            Attractor Loops
          </Title>
          <Text type="secondary">
            Create, configure, and assign attractor loop screens for kiosks
            across stores.
          </Text>
        </div>
      </div>

      {/* Filter / Action Bar */}
      <Space style={{ marginBottom: 16 }} wrap>
        <Search
          placeholder="Search attractorLoops by name"
          onSearch={handleSearch}
          onChange={handleSearchChange}
          style={{ width: 300 }}
          allowClear
        />
        <Segmented
          value={statusFilter}
          onChange={(val) => handleStatusFilterChange(val as string)}
          options={[
            { label: "All", value: "all" },
            { label: "Active", value: "active" },
            { label: "Inactive", value: "inactive" },
          ]}
        />
        <Popconfirm
          placement="bottomRight"
          title="Create New Attractor Loop"
          description={
            <div style={{ marginTop: "8px", minWidth: "220px" }}>
              <Text
                strong
                style={{
                  display: "block",
                  marginBottom: "4px",
                  fontSize: "13px",
                }}
              >
                Loop Name
              </Text>
              <Input
                placeholder="e.g. Summer Promo Attractor"
                value={newLoopName}
                onChange={(e) => setNewLoopName(e.target.value)}
                size="middle"
                autoFocus
                disabled={isReadOnly}
              />
            </div>
          }
          open={isCreatePopconfirmOpen}
          onConfirm={handleCreateLoop}
          okButtonProps={{ 
            loading: createLoading,
            style: isReadOnly ? { display: 'none' } : undefined 
          }}
          onCancel={() => {
            setIsCreatePopconfirmOpen(false);
            setNewLoopName("");
          }}
          okText="Create"
          cancelText="Cancel"
        >
          <Button
            color="primary"
            variant="filled"
            icon={<PlusOutlined />}
            onClick={() => setIsCreatePopconfirmOpen(true)}
          >
            Create Attractor Loop
          </Button>
        </Popconfirm>
      </Space>

      {/* attractorLoops List Table */}
      {loading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <AttractorTable
          data={attractorLoops}
          loading={loading}
          currentPage={currentPage}
          pageSize={pageSize}
          totalCount={totalCount}
          onTableChange={handleTableChange}
          onEdit={handleOpenEdit}
          onDelete={handleDeleteAttractorLoop}
        />
      )}
    </div>
  );
};

export default Attractor;
